import _ from 'lodash';
import { IOption, SkipComparison } from './node/claim/file-claim/question';
import { PolicyExtended, PolicySlim } from './node/policy/policy.model';
import { Status } from './node/tia-rest/model/response/policy.response.model';
import { addMessageWithGroup, SentryGroup } from './sentry';
import { getExpressiveDateString } from './date';
import {
  ImageID,
  MetadataFiltering,
  ParsedQuestion,
  QuestionImageIds,
  TiaEntityAndField,
} from './node/sanity/claimQuestions.types';
import {
  ChatEnums,
  ChatMessage,
  SanityClaimQuestionStore,
} from 'stores/sanity-claim-question-store';

export const buildPolicyObjectOptions = (policy: PolicyExtended) => {
  if (!policy) {
    return [];
  }
  return policy.policyLines[0].policyObjects
    .sort((a, b) => a.shortDescription.localeCompare(b.shortDescription))
    .map<IOption>((object) => ({
      title: object.shortDescription,
      value: object.id,
      avatar: policy.metadata.icon.imageUrl,
    }));
};

export const buildFilClaimInsuranceOptions = (policies: PolicySlim[]) => {
  if (!policies) {
    return [];
  }
  return policies
    .filter((p) => p.status === Status.ACTIVE)
    .map(
      (p): IOption => ({
        title: p.metadata.name,
        subtitle: p.policyLines[0].shortDescription,
        avatar: p.metadata?.icon?.imageUrl,
        value: p.policyId,
        productLineId: p.policyLines[0].productLineId,
      }),
    );
};

export const evaluateSkipCondition = (
  skipComparisons: SkipComparison[],
  values: { [key: string]: string },
  selectedPolicy: PolicyExtended,
) => {
  if (!skipComparisons) {
    return undefined;
  }

  const results = skipComparisons.reduce<string[]>((acc, skipComparison) => {
    const operator = skipComparison.operator;

    const comparisonResults = skipComparison.conditions.map((s) => {
      const skipValues = {
        ...values,
        selectedPolicy: selectedPolicy,
        objectCount: selectedPolicy?.policyLines[0]?.policyObjects.length,
        activeRisks: selectedPolicy?.policyLines[0]?.policyObjects
          ?.map((o) =>
            o.objectRiskSlaves
              .filter((o) => o.riskCovered)
              .map((r) => r.riskNo.toString()),
          )
          ?.flat(),
      };

      const value = _.get(skipValues, s.fieldId) as string;
      const type = s.type;

      if (Array.isArray(s.value)) {
        switch (type) {
          case 'in':
            return s.value.includes(value);
          case 'notIn':
            return !s.value.includes(value);
          default:
            const message = `Unsupported operation ${type} when comparing arrays`;
            addMessageWithGroup({
              group: SentryGroup.claim,
              message: message,
              printToConsole: true,
              consoleMessage: message,
              tag: 'evaluateSkipCondition',
            });
            return undefined;
        }
      } else {
        const valueEqualsFieldValue = s.value === value;
        switch (type) {
          case 'equals':
            return valueEqualsFieldValue;
          case 'notEquals':
            return !valueEqualsFieldValue;
          case 'startsWith':
            return value.startsWith(s.value?.toString());
          case 'greaterThan':
            return Number(value) > Number(s.value);
          case 'lessThan':
            return Number(value) < Number(s.value);
          default:
            const message = `Unsupported operation ${type} when comparing strings`;
            addMessageWithGroup({
              group: SentryGroup.claim,
              message: message,
              printToConsole: true,
              consoleMessage: message,
              tag: 'evaluateSkipCondition',
            });
            return undefined;
        }
      }
    });

    const filteredComparisonResults = comparisonResults.filter(
      (s) => s !== undefined,
    );
    switch (operator) {
      case 'and':
        if (filteredComparisonResults.every((s) => s)) {
          acc.push(skipComparison.skipToFieldId);
        }
        break;
      case 'or':
        if (filteredComparisonResults.some((s) => s)) {
          acc.push(skipComparison.skipToFieldId);
        }
        break;
      default:
        const message = `Unknown operator ${operator}`;
        addMessageWithGroup({
          group: SentryGroup.claim,
          message: message,
          printToConsole: true,
          consoleMessage: message,
          tag: 'evaluateSkipConditionOperator',
        });
    }

    return acc;
  }, []);

  if (results.length > 0) {
    return results[0];
  }

  return undefined;
};

export const getMappedTiaEntity = (
  currentQuestion: ParsedQuestion | ChatMessage,
): string | null => {
  if (!currentQuestion) {
    return null;
  }

  let result: string | null;
  if (!currentQuestion) {
    return null;
  }
  if (
    'claimTaskListOptions' in currentQuestion &&
    currentQuestion.claimTaskListOptions
  ) {
    result = ChatEnums.CLAIM_TASK_LIST_OPTIONS;
  } else if (
    'claimTaskListOptions' in currentQuestion &&
    currentQuestion.caseItemOptions
  ) {
    result = ChatEnums.CASE_ITEM_OPTIONS;
  } else {
    result =
      currentQuestion.tiaEntityAndField?.entity &&
      currentQuestion.tiaEntityAndField?.fieldName
        ? `${currentQuestion.tiaEntityAndField.entity}.${currentQuestion.tiaEntityAndField.fieldName}`
        : currentQuestion.questionId;
  }
  return result;
};

export const getMappedMetadataKey = (key: string) => {
  return `${ChatEnums.METADATA}.${key}`;
};

//TODO: probably can be removed.
export const getMappedTiaExceptionalValue = (
  tiaEntityAndField: TiaEntityAndField,
  exceptionalValue: string,
): string | null => {
  return tiaEntityAndField && exceptionalValue
    ? `${tiaEntityAndField.entity}.${exceptionalValue}`
    : null;
};

export const getMessageTextNew = (
  currentQuestion: ParsedQuestion,
  text: any,
) => {
  const tiaEntityMap = getMappedTiaEntity(currentQuestion);

  // Handle date type
  if (currentQuestion.inputType === 'date') {
    const value = getValueFromPath(text, tiaEntityMap);
    return getExpressiveDateString(value);
  }

  // Handle when case item options are provided
  if (currentQuestion.caseItemOptions) {
    const key = getMappedMetadataKey(ChatEnums.CASE_ITEM_OPTIONS);
    return getValueFromPath(text, key)?.title;
  }

  if (currentQuestion.claimTaskListOptions) {
    const key = getMappedMetadataKey(ChatEnums.CLAIM_TASK_LIST_OPTIONS);
    const res = getValueFromPath(text, key);
    // Array handling (this happens when images have claim list options in sanity)
    if (Array.isArray(res)) {
      return res.map((v) => `- ${v.title}`).join('\n');
    }
    return getValueFromPath(text, key)?.title;
  }
  // Should handle what's left
  const key = getMappedMetadataKey(tiaEntityMap);
  const value = getValueFromPath(text, key);

  if (currentQuestion.options?.length) {
    const optionValue = currentQuestion.options.find(
      (opt) => opt.value === value,
    );
    return optionValue?.title;
  }

  if (currentQuestion.inputType === 'file') {
    if (Array.isArray(value)) {
      return value.map((f) => `- ${f.name}`).join('\n');
    }
    return fallbackText.find((f) => f.type === currentQuestion.inputType).text;
  }
  // Image handling
  if (Array.isArray(value)) {
    return value.map((v) => `- ${v.title}`).join('\n');
  }
  return value?.title;
};

const fallbackText = [
  {
    type: 'file',
    text: 'Det ser ud til, at du ikke har tilføjet bilag til din skade. Du kan tilføje det nu ved at redigere din skade – eller senere via Min side.',
  },
];

export const getFilteredMetadata = (
  options: IOption[],
  metadataFiltering: MetadataFiltering[],
): IOption[] => {
  if (!metadataFiltering || !options) return options;
  const normalizedFilter = metadataFiltering.map((mFilter) => {
    return {
      fieldName: mFilter.fieldName,
      values: new Set(mFilter.fieldValues),
    };
  });

  return options.filter((item) => {
    return normalizedFilter.every((nFilter) => {
      return nFilter.values.has(item[nFilter.fieldName]);
    });
  });
};

export const getValueFromPath = (values: Record<string, any>, path: string) => {
  if (!path.includes('.')) {
    return values[path];
  }
  return path.split('.').reduce((prev, curr) => {
    return prev && prev[curr];
  }, values);
};

function evaluateComparison(operator, leftValue, rightValue) {
  switch (operator) {
    case '==':
      return leftValue == rightValue;
    case '!=':
      return leftValue != rightValue;
    case '>':
      return leftValue > rightValue;
    case '>=':
      return leftValue >= rightValue;
    case '<':
      return leftValue < rightValue;
    case '<=':
      return leftValue <= rightValue;
    case 'regex':
      return new RegExp(rightValue).test(leftValue);
    case 'null':
      return leftValue === null || leftValue === undefined;
    case 'notNull':
      return leftValue !== null && leftValue !== undefined;
    default:
      return false;
  }
}

type ComparisonNode = {
  operatorValue?: null;
  fieldValues: { operator: string; value: any }[] | null;
  reference: { _id: string } | null;
};

type OperatorNode = {
  operatorValue: 'AND' | 'OR';
  conditions: ConditionNode[];
};

type ConditionNode = ComparisonNode | OperatorNode;

function buildExpressionTree(renderIf: any[]): ConditionNode | null {
  if (!renderIf || renderIf.length === 0) return null;

  let andGroups: ConditionNode[] = [];
  let currentAndGroup: ConditionNode[] = [];

  for (const item of renderIf) {
    if (item.operatorValue === 'AND') {
      continue; // AND is implicit inside group
    } else if (item.operatorValue === 'OR') {
      // close current AND group
      if (currentAndGroup.length === 1) {
        andGroups.push(currentAndGroup[0]);
      } else if (currentAndGroup.length > 1) {
        andGroups.push({ operatorValue: 'AND', conditions: currentAndGroup });
      }
      currentAndGroup = [];
    } else {
      // it's a comparison node
      currentAndGroup.push(item as ComparisonNode);
    }
  }

  // push last group
  if (currentAndGroup.length === 1) {
    andGroups.push(currentAndGroup[0]);
  } else if (currentAndGroup.length > 1) {
    andGroups.push({ operatorValue: 'AND', conditions: currentAndGroup });
  }

  if (andGroups.length === 1) return andGroups[0];
  return { operatorValue: 'OR', conditions: andGroups };
}

function evaluateCondition(
  condition: ConditionNode,
  submittedValues: SanityClaimQuestionStore['submittedValues'],
): boolean {
  if (condition.operatorValue === 'AND' || condition.operatorValue === 'OR') {
    const results = (condition as OperatorNode).conditions.map((c) =>
      evaluateCondition(c, submittedValues),
    );
    return condition.operatorValue === 'AND'
      ? results.every(Boolean)
      : results.some(Boolean);
  }

  if (
    !(condition as ComparisonNode).fieldValues ||
    !(condition as ComparisonNode).reference
  )
    return false;
  const refId = (condition as ComparisonNode).reference._id;
  const refValue = submittedValues?.[refId]?.value;
  if (!refId || refValue === undefined) return false;

  return (condition as ComparisonNode).fieldValues.some((f) =>
    evaluateComparison(f.operator, f.value, refValue),
  );
}

export const shouldRenderQuestion = (
  question: ParsedQuestion,
  submittedValues: SanityClaimQuestionStore['submittedValues'],
): boolean => {
  if (!question.renderIf) return true;

  const tree = buildExpressionTree(question.renderIf);
  if (!tree) return true;

  return evaluateCondition(tree, submittedValues);
};

export const dynamicAreaClickEvent = (
  imageMetadata: QuestionImageIds,
  exists: boolean,
) => {
  const { childIds = [] } = imageMetadata;

  const event = new CustomEvent<ImageID[]>('dynamicAreaClick', {
    detail: !exists ? childIds : null,
  });
  window.dispatchEvent(event);
};

export const isAreaSelected = (
  selectedIds: string[],
  imageMetaData: QuestionImageIds,
) => {
  if (imageMetaData.hasOwnProperty('childIds')) {
    return imageMetaData.childIds.some((child) =>
      selectedIds.includes(child.id),
    );
  } else {
    return selectedIds.includes(imageMetaData.id);
  }
};

export const parseSvgImage = (
  svgMarkup: string[],
  selectedAreas: ImageID[],
  color: string,
): string[] => {
  const parser = new DOMParser();

  const updatedSvgs: SVGSVGElement[] = [];

  svgMarkup.forEach((svg) => {
    const doc = parser.parseFromString(svg, 'image/svg+xml');
    const svgElement = doc.documentElement as unknown as SVGSVGElement;

    selectedAreas.forEach((selectedArea) => {
      const element = svgElement.getElementById(
        selectedArea.id,
      ) as unknown as SVGElement;
      if (!element) return;

      element.style.setProperty('fill', color);
    });
    updatedSvgs.push(svgElement);
  });

  return updatedSvgs.map((svg) => svg.outerHTML);
};
